import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/api';

// ChecklistsPanel — operator runbooks (Daily ops / Weekly health / Deployment)
// rendered as server-persisted, auditable checklists. Admin-only on the backend.
//   GET  /strategies/api/checklists/daily|weekly/        -> current run + items
//   GET  /strategies/api/checklists/deployment/...       -> procedures / history / run
//   POST /strategies/api/checklists/item/                -> tick / note one item
//   POST /strategies/api/checklists/run/<id>/close/      -> sign off / reopen

const SUB_TABS = [
  { id: 'daily', label: 'Daily', color: '#089981' },
  { id: 'weekly', label: 'Weekly', color: '#2962ff' },
  { id: 'deployment', label: 'Deployment', color: '#e09b00' },
];

export default function ChecklistsPanel() {
  const [sub, setSub] = useState('daily');
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--tv-text)' }}>Operational Checklists</h2>
        <span style={{ fontSize: 11, color: 'var(--tv-text-muted)' }}>
          Server-persisted &amp; audited · admin-only
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {SUB_TABS.map(t => (
          <button key={t.id} type="button" onClick={() => setSub(t.id)} style={{
            padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${sub === t.id ? t.color : 'var(--tv-border)'}`,
            background: sub === t.id ? 'var(--tv-surface-hi)' : 'var(--tv-surface)',
            color: sub === t.id ? t.color : 'var(--tv-text-dim)', borderRadius: 7,
          }}>{t.label}</button>
        ))}
      </div>

      {sub === 'daily' && <PeriodicChecklist type="daily" endpoint="/strategies/api/checklists/daily/" />}
      {sub === 'weekly' && <PeriodicChecklist type="weekly" endpoint="/strategies/api/checklists/weekly/" />}
      {sub === 'deployment' && <DeploymentChecklist />}
    </div>
  );
}

// ───────────────────────── Daily / Weekly ─────────────────────────

function PeriodicChecklist({ type, endpoint }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [viewingRun, setViewingRun] = useState(null); // a past run payload

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(endpoint);
      const j = await res.json();
      if (j.status === 'success') { setData(j); setError(''); }
      else setError(j.message || `HTTP ${res.status}`);
    } catch { setError('Connection failed'); }
    finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => { load(); setViewingRun(null); }, [load]);

  const loadHistory = async () => {
    try {
      const res = await authFetch(`/strategies/api/checklists/history/?type=${type}`);
      const j = await res.json();
      if (j.status === 'success') setHistory(j.runs);
    } catch { /* ignore */ }
  };

  const openPastRun = async (runId) => {
    try {
      const res = await authFetch(`/strategies/api/checklists/history/?type=${type}&run_id=${runId}`);
      const j = await res.json();
      if (j.status === 'success') setViewingRun(j);
    } catch { /* ignore */ }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorBox msg={error} />;

  const active = viewingRun || data;
  const readOnly = !!viewingRun || active?.run?.status === 'closed';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <Progress summary={active.summary} />
        <RunMeta run={active.run} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => { const n = !showHistory; setShowHistory(n); if (n) loadHistory(); }} style={btn()}>
            {showHistory ? 'Hide history' : 'History'}
          </button>
          {viewingRun
            ? <button type="button" onClick={() => setViewingRun(null)} style={btn('var(--tv-blue)')}>Back to today</button>
            : <button type="button" onClick={load} style={btn('var(--tv-blue)')}>Refresh</button>}
        </div>
      </div>

      {showHistory && (
        <HistoryList runs={history} onPick={openPastRun} activeId={viewingRun?.run?.id} />
      )}

      <RunBody payload={active} readOnly={readOnly} onChanged={viewingRun ? openPastRun : load} />

      {!viewingRun && active?.run && (
        <CloseBar run={active.run} summary={active.summary} onChanged={load} />
      )}
    </div>
  );
}

// ───────────────────────── Deployment ─────────────────────────

function DeploymentChecklist() {
  const [procedures, setProcedures] = useState([]);
  const [history, setHistory] = useState([]);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newProc, setNewProc] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, hRes] = await Promise.all([
        authFetch('/strategies/api/checklists/deployment/procedures/'),
        authFetch('/strategies/api/checklists/deployment/'),
      ]);
      const p = await pRes.json(); const h = await hRes.json();
      if (p.status === 'success') { setProcedures(p.procedures); setNewProc(prev => prev || p.procedures[0]); }
      if (h.status === 'success') setHistory(h.runs);
      setError('');
    } catch { setError('Connection failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openRun = async (runId) => {
    try {
      const res = await authFetch(`/strategies/api/checklists/deployment/?run_id=${runId}`);
      const j = await res.json();
      if (j.status === 'success') setRun(j);
    } catch { /* ignore */ }
  };

  const startRun = async () => {
    try {
      const res = await authFetch('/strategies/api/checklists/deployment/', {
        method: 'POST',
        body: JSON.stringify({ procedure: newProc, label: newLabel }),
      });
      const j = await res.json();
      if (j.status === 'success') { setRun(j); setNewLabel(''); loadList(); }
      else setError(j.message || 'Failed to start run');
    } catch { setError('Connection failed'); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorBox msg={error} />;

  if (run) {
    const readOnly = run.run?.status === 'closed';
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <Progress summary={run.summary} />
          <RunMeta run={run.run} />
          <button type="button" onClick={() => { setRun(null); loadList(); }} style={{ ...btn('var(--tv-blue)'), marginLeft: 'auto' }}>
            ← All deployments
          </button>
        </div>
        <RunBody payload={run} readOnly={readOnly} onChanged={() => openRun(run.run.id)} />
        <CloseBar run={run.run} summary={run.summary} onChanged={() => openRun(run.run.id)} />
      </div>
    );
  }

  return (
    <div>
      <div style={{
        background: 'var(--tv-surface)', border: '1px solid var(--tv-border)',
        borderRadius: 8, padding: 16, marginBottom: 20,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tv-text)', marginBottom: 12 }}>Start a deployment run</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={lblStyle}>Procedure</label>
            <select value={newProc} onChange={e => setNewProc(e.target.value)} style={inputStyle}>
              {procedures.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
            <label style={lblStyle}>Label (e.g. release tag / change summary)</label>
            <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="v20260625-abc123 — NUIT emitter wiring" style={inputStyle} />
          </div>
          <button type="button" onClick={startRun} style={{ ...btn('#e09b00'), height: 36 }}>+ Start run</button>
        </div>
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tv-text)', marginBottom: 10 }}>Deployment history</div>
      {history.length === 0
        ? <Empty msg="No deployment runs yet." />
        : <HistoryList runs={history} onPick={openRun} deployment />}
    </div>
  );
}

// ───────────────────────── shared run body ─────────────────────────

function RunBody({ payload, readOnly, onChanged }) {
  if (!payload?.groups) return null;
  return (
    <div>
      {payload.groups.map((g) => (
        <div key={g.group || 'main'} style={{ marginBottom: 22 }}>
          {g.group && (
            <div style={{
              fontSize: 13, fontWeight: 700, color: 'var(--tv-text)',
              padding: '8px 12px', background: 'var(--tv-surface-hi)',
              border: '1px solid var(--tv-border)', borderRadius: 6, marginBottom: 8,
            }}>{g.group}</div>
          )}
          {g.sections.map((s) => (
            <div key={s.section} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--tv-text-muted)', margin: '8px 4px 6px' }}>
                {s.section}
              </div>
              {s.items.map((it) => (
                <ChecklistItem key={it.key} runId={payload.run.id} item={it} readOnly={readOnly} onChanged={onChanged} />
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ChecklistItem({ runId, item, readOnly, onChanged }) {
  const [done, setDone] = useState(item.done);
  const [notes, setNotes] = useState(item.notes || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDone(item.done); setNotes(item.notes || ''); }, [item.done, item.notes]);

  const post = async (body) => {
    setSaving(true);
    try {
      const res = await authFetch('/strategies/api/checklists/item/', {
        method: 'POST',
        body: JSON.stringify({ run_id: runId, item_key: item.key, ...body }),
      });
      const j = await res.json();
      if (j.status === 'success' && onChanged) onChanged(runId);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const toggle = () => {
    if (readOnly || saving) return;
    const next = !done;
    setDone(next);
    post({ done: next });
  };

  const saveNotes = () => { setEditing(false); if (notes !== (item.notes || '')) post({ notes }); };

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px',
      borderBottom: '1px solid var(--tv-border)',
      background: done ? 'var(--tv-bull-tint, rgba(8,153,129,0.06))' : 'transparent',
    }}>
      <button type="button" onClick={toggle} disabled={readOnly} style={{
        flexShrink: 0, width: 20, height: 20, marginTop: 1, cursor: readOnly ? 'default' : 'pointer',
        borderRadius: 4, border: `1.5px solid ${done ? '#089981' : 'var(--tv-border)'}`,
        background: done ? '#089981' : 'var(--tv-surface)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, lineHeight: 1,
      }} title={done ? 'Mark not done' : 'Mark done'}>{done ? '✓' : ''}</button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--tv-text)', textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.7 : 1 }}>
          {item.step}
        </div>

        {editing ? (
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input autoFocus value={notes} onChange={e => setNotes(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveNotes(); }}
              placeholder="Add a note…" style={{ ...inputStyle, flex: 1, fontSize: 12 }} />
            <button type="button" onClick={saveNotes} style={btn('var(--tv-blue)')}>Save</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
            {notes && <span style={{ fontSize: 12, color: 'var(--tv-text-dim)', fontStyle: 'italic' }}>“{notes}”</span>}
            {!readOnly && (
              <button type="button" onClick={() => setEditing(true)} style={linkBtn}>
                {notes ? 'edit note' : '+ note'}
              </button>
            )}
            {done && item.completed_by_name && (
              <span style={{ fontSize: 11, color: 'var(--tv-text-muted)', fontFamily: 'Roboto Mono, monospace' }}>
                ✓ {item.completed_by_name}{item.completed_at ? ` · ${fmt(item.completed_at)}` : ''}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────── small pieces ─────────────────────────

function Progress({ summary }) {
  if (!summary) return null;
  const { total, done } = summary;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const complete = total > 0 && done === total;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 130, height: 8, background: 'var(--tv-border)', borderRadius: 5, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: complete ? '#089981' : 'var(--tv-blue)', transition: 'width .2s' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: complete ? '#089981' : 'var(--tv-text)' }}>
        {done}/{total}{complete ? ' ✓ complete' : ''}
      </span>
    </div>
  );
}

function RunMeta({ run }) {
  if (!run) return null;
  return (
    <span style={{ fontSize: 11, color: 'var(--tv-text-muted)', fontFamily: 'Roboto Mono, monospace' }}>
      {run.label ? `${run.label} · ` : ''}{run.period_key}
      {run.status === 'closed' ? ' · CLOSED' : ''}
      {run.created_by_name ? ` · opened by ${run.created_by_name}` : ''}
    </span>
  );
}

function CloseBar({ run, summary, onChanged }) {
  const [busy, setBusy] = useState(false);
  const act = async (reopen) => {
    setBusy(true);
    try {
      await authFetch(`/strategies/api/checklists/run/${run.id}/close/`, {
        method: 'POST', body: JSON.stringify({ reopen }),
      });
      if (onChanged) onChanged();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };
  const allDone = summary && summary.total > 0 && summary.done === summary.total;
  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--tv-border)', display: 'flex', alignItems: 'center', gap: 10 }}>
      {run.status === 'closed' ? (
        <>
          <span style={{ fontSize: 12, color: '#089981', fontWeight: 600 }}>Signed off{run.closed_at ? ` · ${fmt(run.closed_at)}` : ''}</span>
          <button type="button" disabled={busy} onClick={() => act(true)} style={btn()}>Reopen</button>
        </>
      ) : (
        <>
          <button type="button" disabled={busy} onClick={() => act(false)} style={btn('#089981')}>
            Sign off &amp; close
          </button>
          {!allDone && <span style={{ fontSize: 11, color: 'var(--tv-text-muted)' }}>You can close before every item is ticked.</span>}
        </>
      )}
    </div>
  );
}

function HistoryList({ runs, onPick, activeId, deployment }) {
  if (!runs || runs.length === 0) return <Empty msg="No history yet." />;
  return (
    <div style={{ border: '1px solid var(--tv-border)', borderRadius: 8, overflow: 'hidden', marginBottom: 18 }}>
      {runs.map(r => (
        <button key={r.id} type="button" onClick={() => onPick(r.id)} style={{
          display: 'flex', width: '100%', alignItems: 'center', gap: 12, padding: '9px 12px',
          background: activeId === r.id ? 'var(--tv-surface-hi)' : 'var(--tv-surface)',
          border: 'none', borderBottom: '1px solid var(--tv-border)', cursor: 'pointer', textAlign: 'left',
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tv-text)', minWidth: 150, fontFamily: 'Roboto Mono, monospace' }}>
            {deployment ? (r.procedure || '—') : r.period_key}
          </span>
          {deployment && r.label && <span style={{ fontSize: 12, color: 'var(--tv-text-dim)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>}
          <span style={{ fontSize: 11, color: 'var(--tv-text-muted)', marginLeft: 'auto' }}>
            {r.done_count}/{r.total}
          </span>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            color: r.status === 'closed' ? '#089981' : 'var(--tv-blue)',
            background: r.status === 'closed' ? 'rgba(8,153,129,0.12)' : 'var(--tv-blue-tint)',
          }}>{r.status === 'closed' ? 'CLOSED' : 'OPEN'}</span>
        </button>
      ))}
    </div>
  );
}

const Loading = () => <div style={{ padding: 30, textAlign: 'center', color: 'var(--tv-text-muted)' }}>Loading…</div>;
const ErrorBox = ({ msg }) => <div style={{ padding: 12, background: 'var(--tv-bear-tint)', border: '1px solid var(--tv-bear)', borderRadius: 6, color: 'var(--tv-bear)', fontSize: 12 }}>{msg}</div>;
const Empty = ({ msg }) => <div style={{ padding: 24, textAlign: 'center', background: 'var(--tv-surface)', border: '1px dashed var(--tv-border)', borderRadius: 8, color: 'var(--tv-text-muted)', fontSize: 13 }}>{msg}</div>;

function fmt(iso) {
  if (!iso) return '';
  // Backend timestamps are already ET-naive wall-clock — render as-is, no tz shift.
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const btn = (color) => ({
  padding: '6px 14px', fontSize: 12, fontWeight: 600,
  border: `1px solid ${color || 'var(--tv-border)'}`, borderRadius: 6, cursor: 'pointer',
  background: 'var(--tv-surface)', color: color || 'var(--tv-text-dim)',
});
const linkBtn = { background: 'none', border: 'none', padding: 0, fontSize: 11, color: 'var(--tv-blue)', cursor: 'pointer', fontWeight: 600 };
const lblStyle = { fontSize: 11, fontWeight: 600, color: 'var(--tv-text-muted)' };
const inputStyle = {
  padding: '7px 10px', fontSize: 13, border: '1px solid var(--tv-border)', borderRadius: 6,
  background: 'var(--tv-surface)', color: 'var(--tv-text)', outline: 'none',
};
